import { useState, useEffect } from 'react';
import { createClientWithConsent } from 'components/newAccountSteps/profileSteps/services/createClientWithConsent';
import { getAllData } from '../utils/getAllData';
import Storage from 'utilities/storage';
import addressHelper from 'utilities/addressHelper/addressHelper';
import {
    mustIncludeJoinDecision,
    mustValidatePostalCode,
    getValidationPostalCodeByCountry,
} from 'utilities/logicByCountry';
import { TRADITIONAL, GBI, ROBO, CREDIT_CARD } from 'constants/userProfile';
import { ONB_BANK_DEPOSIT_OPEN } from 'constants/onboardingTypes';
import { COUNTRY_CODE_ES } from 'commonsConstants/countryCodes';
import {
    COUNTRY_DOCUMENT_TYPE_NIE_LABEL,
    COUNTRY_DOCUMENT_TYPE_DNI_LABEL,
    COUNTRY_DOCUMENT_TYPE_NIE,
    COUNTRY_DOCUMENT_TYPE_DNI,
} from 'constants/multiLanguage';
import {
    doTagClienVerifyError,
    doTagCompleteYourData,
    doTagCompleteYourDataJointHolders,
    doTagCompleteYourDataJointHoldersDepositAdvise,
    doTagCompleteYourDataJointHoldersSuccessful,
    doTagCompleteYourDataSuccessful,
    doTagCreateNewClient,
    doTagCreditCardCheckboxChecked,
    doTagCreditCardCheckboxUnchecked,
    doTagSubmitError,
} from '../tagging';

export const useSubmit = ({
    actions,
    consentsActions,
    createActions,
    currentIndex,
    docNumberEncrypted,
    formActions,
    formValidations,
    goToErrorsView,
    ibanActions,
    immStepsState,
    options,
    preLoaderActions,
    redirectErrors,
    scorings,
    steps,
    userProfile,
    userProfileForm,
}) => {
    const [isClicked, setIsClicked] = useState(true);

    const {
        accessToken,
        addressNumber,
        country,
        // eslint-disable-next-line camelcase
        creditCardOffer_accepted,
        birthCountry,
        investmentType,
        jointHolderDecision,
        nameOfRoad,
        onboardingType,
        postalCode,
        province,
        recordId,
        town,
        userId,
        universiaName,
        documentType,
    } = userProfile;

    const {
        changeCurrentStep,
        clientCardConfigVerify,
        clientVerify,
        enableFinishedAndNextStep,
        finalizeStep,
        savePersonalInformation,
    } = actions;

    const [isVerified, setIsVerified] = useState(false);

    const proceedToNextStep = () => {
        changeCurrentStep({
            index: currentIndex + 1,
        });
    };
    const gotoNextStep = () => {
        finalizeStep({
            index: currentIndex,
            finished: true,
        });
        enableFinishedAndNextStep();
        proceedToNextStep();
    };
    const createClient = accessToken => {
        const finishAction = () => {
            gotoNextStep();
            preLoaderActions.hidePreLoader();
        };
        const onCreateClient = userId => {
            const userAlreadySaved = Storage.cookies.get('roboAnalitycs', true);
            if (!userAlreadySaved) {
                doTagCreateNewClient({ userProfile, userId });

                Storage.cookies.set('roboAnalitycs', true);
            }
        };
        const onError = () => {
            preLoaderActions.hidePreLoader();
        };
        createClientWithConsent({ createActions, consentsActions }, accessToken, onCreateClient, finishAction, onError);
    };
    const handleVerify = () => {
        clientVerify(accessToken, (verified, error) => {
            if (verified) {
                ibanActions.toggleShowIban(false);
                if (
                    mustIncludeJoinDecision() ||
                    jointHolderDecision === 'false' ||
                    investmentType === ROBO ||
                    investmentType === GBI ||
                    investmentType === CREDIT_CARD
                ) {
                    clientCardConfigVerify(accessToken, (verifiedOk, error) => {
                        if (verifiedOk) {
                            if (investmentType === ROBO || investmentType === GBI) {
                                createClient(accessToken);
                            } else {
                                gotoNextStep();
                                preLoaderActions.hidePreLoader();
                            }
                            doTagCompleteYourData({
                                docNumberEncrypted,
                                investmentType,
                                recordId,
                            });
                            doTagCompleteYourDataSuccessful(investmentType);
                        } else {
                            preLoaderActions.hidePreLoader();
                            if (error.body.errors['6002']) {
                                goToErrorsView({ errorCode: 6002 });
                            }
                        }
                    });
                } else {
                    preLoaderActions.hidePreLoader();
                }
            } else {
                preLoaderActions.hidePreLoader();
                doTagClienVerifyError({
                    body: error.body,
                    statusCode: error.statusCode,
                });
                if (error.body.errors['0243'] || error.body.errors['0242']) {
                    formActions.setValidity('userProfile.postalCode', { zip: false });
                }
                if (error.body.errors['6002']) {
                    goToErrorsView({ errorCode: 6002 });
                }
            }
        });
    };
    const checkboxEvent = () => {
        const ML2 = scorings?.getIn(['ml2', 'scoringStatus']) === 'approved';

        if (!ML2) return;

        // eslint-disable-next-line camelcase
        if (creditCardOffer_accepted) {
            doTagCreditCardCheckboxChecked({ docNumberEncrypted, investmentType });
        } else {
            doTagCreditCardCheckboxUnchecked({ docNumberEncrypted, investmentType });
        }
    };

    const handleSavePersonalInformationError = error => {
        doTagSubmitError({
            body: error.body,
            statusCode: error.statusCode,
        });
        redirectErrors(error);
        setIsClicked(false);
    };

    const handleJointHolderData = () => {
        const isDeposit = investmentType === TRADITIONAL && onboardingType === ONB_BANK_DEPOSIT_OPEN;

        doTagCompleteYourDataJointHolders({ docNumberEncrypted, investmentType, recordId });
        doTagCompleteYourDataJointHoldersSuccessful(investmentType);

        if (isDeposit) {
            formActions.change('userProfile.showJointHolderDepositAdvise', true);
            doTagCompleteYourDataJointHoldersDepositAdvise();
        }

        formActions.change('userProfile.showJointHolder', true);
        formActions.change('userProfile.isJointHolderAccount', true);

        setIsClicked(false);
        window.scrollTo(0, 0);
    };

    const handleVerified = () => {
        const finishedStep = steps.get(0).get('finished');
        if (finishedStep) {
            setIsVerified(true);
        }
    };
    const saveUserProfileData = data => {
        const showLoader = true;
        const formattedAddress = addressHelper.generateFormattedAddress(
            nameOfRoad,
            addressNumber,
            postalCode,
            town,
            province,
            country,
            options.get('cities'),
            options.get('countries')
        );

        formActions.change('userProfile.address', formattedAddress);
        savePersonalInformation(data, accessToken, showLoader, (saved, error) => {
            if (saved) {
                handleVerify();
            } else {
                handleSavePersonalInformationError(error);
            }
        });

        return jointHolderDecision === 'true' ? handleJointHolderData() : handleVerified();
    };

    const handleValidForm = data => {
        if ((investmentType === ROBO || investmentType === GBI) && userId) {
            return proceedToNextStep();
        }

        if (mustValidatePostalCode() && !getValidationPostalCodeByCountry(userProfile)) {
            return formActions.setValidity('userProfile.postalCode', { zip: false });
        }

        saveUserProfileData(data);
    };

    const handleSubmit = () => {
        const disabled = immStepsState.get('readOnlyForms');
        const data = getAllData(userProfile);

        setIsClicked(true);

        if (!disabled && userProfileForm.$form.valid && !isVerified) {
            handleValidForm(data);
        } else {
            proceedToNextStep();
        }

        checkboxEvent();
    };

    const handleEnableSubmit = () => {
        const fieldsToValidate = [...formValidations.primaries, ...formValidations.firstAddress];

        if (
            documentType === COUNTRY_DOCUMENT_TYPE_DNI_LABEL ||
            documentType === COUNTRY_DOCUMENT_TYPE_NIE_LABEL ||
            documentType === COUNTRY_DOCUMENT_TYPE_DNI ||
            documentType === COUNTRY_DOCUMENT_TYPE_NIE
        ) {
            if (investmentType === TRADITIONAL && !universiaName) {
                fieldsToValidate.push('jointHolderDecision');
            }
        }

        if (birthCountry === COUNTRY_CODE_ES) {
            fieldsToValidate.push('birthProvince');
        }

        const clicked = fieldsToValidate.some(prop => {
            const field = userProfileForm[prop];

            return typeof field === 'undefined' || !field.value || !field.valid;
        });

        setIsClicked(clicked);
    };

    useEffect(() => {
        userProfileForm.$form.valid && handleEnableSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfileForm.$form.valid]);

    return { isClicked, handleSubmit, handleEnableSubmit };
};
